import { createApi } from '@reduxjs/toolkit/query/react';
import { authBaseQuery } from '../../app/authBaseQuery';
import { parseLaravelPaginator } from '../../lib/parseLaravelPaginator';
import { unwrapMaybeWrapped } from '../../lib/unwrapResource';
import {
  normalizeInvestment,
  normalizePayment,
  normalizePayout,
  normalizePayoutSummary,
  normalizeRolloverHistoryEntry,
} from './normalizeInvestment';

/**
 * Real-backend investor-facing "My Investments" surface — read-only,
 * scoped automatically to the logged-in investor server-side (same
 * pattern as the investment plan catalog: any authenticated user, no
 * extra permission check). There's no investor-initiated create/pay
 * endpoint yet (the Instant/Offline subscription paths aren't exposed) —
 * this slice only ever GETs.
 *
 * A GET for an investment or its payments that belongs to someone else
 * returns a plain 404, same as one that doesn't exist at all — don't try
 * to tell those apart (see pages/investor/InvestmentDetail.jsx).
 *
 * Same bare-paginator convention as the admin investments surface —
 * shares parseLaravelPaginator and the normalizeInvestment/normalizePayment
 * mappers with adminInvestmentsApi.js, since the resource shapes are
 * identical, just scoped differently server-side.
 */
export const investorInvestmentsApi = createApi({
  reducerPath: 'investorInvestmentsApi',
  baseQuery: authBaseQuery(),
  tagTypes: ['MyInvestment', 'MyInvestmentPayment', 'MyInvestmentPayout', 'MyInvestmentRollover'],
  endpoints: (builder) => ({
    // GET /investor/investments?status=&per_page=&page=&has_pending_payout=
    // `has_pending_payout` must be sent as 1/0, never 'true'/'false' —
    // Laravel's boolean validation 422s on the string form.
    listMyInvestments: builder.query({
      query: ({ status, page, per_page, has_pending_payout } = {}) => ({
        url: '/investor/investments',
        method: 'GET',
        params: { status, page, per_page, has_pending_payout: has_pending_payout ? 1 : undefined },
      }),
      transformResponse: (response) => {
        const { items, meta, parseError } = parseLaravelPaginator(response, { context: 'investorInvestmentsApi:list' });
        return { investments: items.map(normalizeInvestment), meta, parseError: !!parseError };
      },
      providesTags: (result) => [
        ...(result?.investments ?? []).map(({ id }) => ({ type: 'MyInvestment', id })),
        { type: 'MyInvestment', id: 'LIST' },
      ],
    }),

    // GET /investor/investments/{investment}
    getMyInvestment: builder.query({
      query: (id) => ({ url: `/investor/investments/${id}`, method: 'GET' }),
      transformResponse: (response) => normalizeInvestment(unwrapMaybeWrapped(response)),
      providesTags: (result, error, id) => [{ type: 'MyInvestment', id }],
    }),

    // GET /investor/investments/{investment}/payments?per_page=
    listMyInvestmentPayments: builder.query({
      query: ({ investmentId, page, per_page } = {}) => ({
        url: `/investor/investments/${investmentId}/payments`,
        method: 'GET',
        params: { page, per_page },
      }),
      transformResponse: (response) => {
        const { items, meta, parseError } = parseLaravelPaginator(response, { context: 'investorInvestmentsApi:payments' });
        return { payments: items.map(normalizePayment), meta, parseError: !!parseError };
      },
      providesTags: (result, error, { investmentId }) => [{ type: 'MyInvestmentPayment', id: investmentId }],
    }),

    // GET /investor/investments/{investment}/payouts?per_page= — v1.13.0,
    // read-only, same ownership scoping/404 behavior as the investment and
    // payments endpoints above. Payouts are a distinct concept from
    // payments (see normalizePayout's comment) — this investor-side view
    // never has a record/create action, only admins record payouts.
    //
    // v1.16.0 adds a top-level `summary` (PayoutSummary), same as the admin
    // side — the authoritative ledger snapshot, never recomputed client-side.
    listMyInvestmentPayouts: builder.query({
      query: ({ investmentId, page, per_page } = {}) => ({
        url: `/investor/investments/${investmentId}/payouts`,
        method: 'GET',
        params: { page, per_page },
      }),
      transformResponse: (response) => {
        const { items, meta, parseError } = parseLaravelPaginator(response, { context: 'investorInvestmentsApi:payouts' });
        return {
          payouts: items.map(normalizePayout),
          summary: normalizePayoutSummary(response?.summary),
          meta,
          parseError: !!parseError,
        };
      },
      providesTags: (result, error, { investmentId }) => [{ type: 'MyInvestmentPayout', id: investmentId }],
    }),

    // GET /investor/investments/{investment}/rollovers?per_page= — v1.17.0,
    // read-only, same ownership scoping/404 behavior as the endpoints
    // above (belongs-to-someone-else looks identical to not-found). No
    // create/cancel mutation on this side — rollover actions are
    // admin-only via manage-investments.
    listMyInvestmentRollovers: builder.query({
      query: ({ investmentId, page, per_page } = {}) => ({
        url: `/investor/investments/${investmentId}/rollovers`,
        method: 'GET',
        params: { page, per_page },
      }),
      transformResponse: (response) => {
        const { items, meta, parseError } = parseLaravelPaginator(response, { context: 'investorInvestmentsApi:rollovers' });
        return { rollovers: items.map(normalizeRolloverHistoryEntry), meta, parseError: !!parseError };
      },
      providesTags: (result, error, { investmentId }) => [{ type: 'MyInvestmentRollover', id: investmentId }],
    }),
  }),
});

export const {
  useListMyInvestmentsQuery,
  useGetMyInvestmentQuery,
  useListMyInvestmentPaymentsQuery,
  useListMyInvestmentPayoutsQuery,
  useListMyInvestmentRolloversQuery,
} = investorInvestmentsApi;
